import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from '../components/AlertDialog';
import CircularLoading from "../components/CircularLoading";
import Grid from '@mui/material/Grid';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import LanguageIcon from '@mui/icons-material/Language';
import BusinessIcon from '@mui/icons-material/Business';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LinkIcon from '@mui/icons-material/Link';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import AppleIcon from '@mui/icons-material/Apple';
import ShopIcon from '@mui/icons-material/Shop';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import { FONT_FAMILY } from "../common/sharedFunctions";

const modernColors = {
  primary: '#4F46E5',
  primaryLight: '#818CF8',
  primaryDark: '#3730A3',
  secondary: '#10B981',
  background: '#F9FAFB',
  surface: '#FFFFFF',
  textPrimary: '#111827',
  textSecondary: '#6B7280',
  divider: '#E5E7EB',
  hover: '#F3F4F6',
  active: '#EEF2FF',
  error: '#EF4444',
  info: '#3B82F6',
  white: '#FFFFFF',
};

const AppInformation = (props) => {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editSettings,
    clearSettingsViewError
  } = api;
  const settingsdata = useSelector(state => state.settingsdata);
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [clicked, setClicked] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (settingsdata.settings) {
      setData(settingsdata.settings);
    }
  }, [settingsdata.settings]);

  const handleTextChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (data.AllowCriticalEditsAdmin) {
      setClicked(true);
      setLoading(true);
      dispatch(editSettings(data));
      setTimeout(() => setLoading(false), 1000);
    } else {
      alert(t('demo_mode'));
    }
  }

  const handleClose = () => {
    setClicked(false);
    setLoading(false);
    dispatch(clearSettingsViewError());
  };

 
  return (
    !data ? <CircularLoading /> :
    <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', backgroundColor: modernColors.surface }}>
      <Typography variant="h5" sx={{ fontWeight: 600, color: modernColors.textPrimary, mb: 3, fontFamily: FONT_FAMILY }}>
         {t('app_info_title')}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                 <Typography variant="h6" sx={{ fontWeight: 500, color: modernColors.textPrimary, mb: 2, fontFamily: FONT_FAMILY }}>
                    {t('app_info')}
                 </Typography>
                 <TextField
                   variant="outlined"
                   margin="normal"
                   required
                   fullWidth
                   id="appName"
                   label={ t('AppName')}
                   name="appName"
                   autoComplete="organization"
                   onChange={handleTextChange}
                   value={data.appName || ''}
                   InputProps={{ startAdornment: <InputAdornment position="start"><InfoIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                   sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
                 <TextField
                   variant="outlined"
                   margin="normal"
                   required
                   fullWidth
                   id="CompanyName"
                   label={t('CompanyName')}
                   name="CompanyName"
                   autoComplete="organization-title"
                   onChange={handleTextChange}
                   value={data.CompanyName || ''}
                   InputProps={{ startAdornment: <InputAdornment position="start"><BusinessIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                   sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
                 <TextField
                   variant="outlined"
                   margin="normal"
                   required
                   fullWidth
                   multiline
                   rows={3}
                   id="CompanyAddress"
                   label={t('CompanyAddress')}
                   name="CompanyAddress"
                   autoComplete="street-address"
                   onChange={handleTextChange}
                   value={data.CompanyAddress || ''}
                   InputProps={{ startAdornment: <InputAdornment position="start" sx={{mt: -5}}><LocationCityIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                   sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
                 <TextField
                   variant="outlined"
                   margin="normal"
                   required
                   fullWidth
                   id="CompanyWebsite"
                   label={t('CompanyWebsite')}
                   name="CompanyWebsite"
                   type="url"
                   autoComplete="url"
                   onChange={handleTextChange}
                   value={data.CompanyWebsite || ''}
                    InputProps={{ startAdornment: <InputAdornment position="start"><LanguageIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
                 <TextField
                   variant="outlined"
                   margin="normal"
                   fullWidth
                   id="contact_email"
                   label={t('contact_email')}
                   name="contact_email"
                   type="email"
                   autoComplete="email"
                   onChange={handleTextChange}
                   value={data.contact_email || ''}
                   InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                   sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
                 <TextField
                   variant="outlined"
                   margin="normal"
                   required
                   fullWidth
                   id="CompanyPhone"
                   label={t('company_phone')}
                   name="CompanyPhone"
                   type="tel"
                   autoComplete="tel"
                   onChange={handleTextChange}
                   value={data.CompanyPhone || ''}
                   InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                   sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
            </Grid>

            <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: modernColors.textPrimary, mb: 2, fontFamily: FONT_FAMILY }}>
                    {t('app_link')}
                </Typography>
                <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    id="CompanyTerms"
                    label={t('terms')}
                    name="CompanyTerms"
                    type="url"
                    autoComplete="url"
                    onChange={handleTextChange}
                    value={data.CompanyTerms || ''}
                    InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
                 <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    id="CompanyTermCondition"
                    label={t('term_condition')}
                    name="CompanyTermCondition"
                    type="url"
                    autoComplete="url"
                    onChange={handleTextChange}
                    value={data.CompanyTermCondition || ''}
                    InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
                 <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="FacebookHandle"
                    label={t('FacebookHandle')}
                    name="FacebookHandle"
                    type="url"
                    autoComplete="url"
                    onChange={handleTextChange}
                    value={data.FacebookHandle || ''}
                    InputProps={{ startAdornment: <InputAdornment position="start"><FacebookIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
                 <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="TwitterHandle"
                    label={t('TwitterHandle')}
                    name="TwitterHandle"
                    type="url"
                    autoComplete="url"
                    onChange={handleTextChange}
                    value={data.TwitterHandle || ''}
                    InputProps={{ startAdornment: <InputAdornment position="start"><TwitterIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
                 <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="InstagramHandle"
                    label={t('InstagramHandle')}
                    name="InstagramHandle"
                    type="url"
                    autoComplete="url"
                    onChange={handleTextChange}
                    value={data.InstagramHandle || ''}
                    InputProps={{ startAdornment: <InputAdornment position="start"><InstagramIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
                 <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="AppleStoreLink"
                    label={t('AppleStoreLink')}
                    name="AppleStoreLink"
                    type="url"
                    autoComplete="url"
                    onChange={handleTextChange}
                    value={data.AppleStoreLink || ''}
                    InputProps={{ startAdornment: <InputAdornment position="start"><AppleIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
                 <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="PlayStoreLink"
                    label={t('PlayStoreLink')}
                    name="PlayStoreLink"
                    type="url"
                    autoComplete="url"
                    onChange={handleTextChange}
                    value={data.PlayStoreLink || ''}
                    InputProps={{ startAdornment: <InputAdornment position="start"><ShopIcon sx={{color: modernColors.textSecondary}} /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                 />
            </Grid>

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} sx={{ color: modernColors.white }}/> : <SaveIcon />}
                sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: '10px',
                    backgroundColor: modernColors.primary,
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&:hover': {
                        backgroundColor: modernColors.primaryDark,
                        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)',
                    }
                }}
              >
                {loading ? t('saving') : t('submit')}
              </Button>
            </Grid>
        </Grid>
      </Box>
      <AlertDialog open={settingsdata.error.flag && clicked} onClose={handleClose}>{t('update_failed')}</AlertDialog>
    </Paper>
  );

}

export default AppInformation;